import { useEffect, useState } from 'react';
import DescriptionBanner from '../Components/DescriptionBanner';
import { Icon } from '../Components/UI/Icon';
import SecurityTabs from '../Components/SecurityTabs';
import useGet from '../Hooks/useGet';
import API_ENDPOINTS from '../Config/api';
import Nis2Risk from '../Components/Nis2Risk';
import AwarenessCard from '../Components/AwarenessCard';
import AwarenessDashboardCard from '../Components/AwarenessDashboardCard';
import AwarenessTrainingStats from '../Components/AwarenessTrainingStats';
import NetworkingSurveillanceSection from '../Components/NetworkingSurveillanceSection';
import ReportExporter from '../Components/ReportExporter';
import getCyberLogIcon from '../Utils/getCyberLogIcon';
import CyberSurveillanceDashboard, { CyberSurveillanceStats } from '../Components/CyberSurveillanceDashboard2';
import VulnerabilityCharts from '../Components/VulnerabilityCharts';
import BrowsingAngelTable from '../Components/Table/BrowsingAngelTable';
import ClipLoader from 'react-spinners/ClipLoader';
import FileAngelTable from '../Components/Table/FileAngelTable';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { fetchWithAuth } from '../Utils/fetchWithAuth';
import { useRecoilValue } from 'recoil';
import { disableForEnterpriseAtom } from '../Atom/disableForEnterpriseAtom';

export default function CyberLog() {

  const { t, i18n } = useTranslation();

  const { fetchedData: questionarioCurrent } = useGet(API_ENDPOINTS.questionarioCurrent);
  const { fetchedData: cyberSurveillanceData } = useGet(API_ENDPOINTS.cyberSurveillance);
  const { fetchedData: browsingAngelStats } = useGet(API_ENDPOINTS.aziendaBrowsingAngelStats);
  const { fetchedData: aziendaFileAngelStats } = useGet(API_ENDPOINTS.aziendaFileAngelStats);
  const { fetchedData: aziendaDettagli } = useGet(API_ENDPOINTS.aziendaDettagli);
  
  // Fetch vulnerabilities for charts
  const [allVulnerabilities, setAllVulnerabilities] = useState([])
  const { fetchedData: allVulnerabilitiesAPI } = useGet(`${API_ENDPOINTS.cyberSurveillanceVulnerability}?page_size=1000`);
  useEffect(() => { 
    if (allVulnerabilitiesAPI?.results) {
      setAllVulnerabilities(allVulnerabilitiesAPI.results)
    }
  }, [allVulnerabilitiesAPI])
  
  // use stable ids for sidebar selection to avoid relying on translated labels as keys
  const SIDEBAR_KEYS = {
    GENERAL: 'generalCompliance',
    AREAS: 'complianceAreas',
    AWARENESS: 'awareness',
    FILE_ANGEL: 'fileAngel',
    BROWSING_ANGEL: 'browsingAngel',
    AWARENESS_TRAINING: 'awarenessTraining',
    NETWORKING_SURVEILLANCE: 'networkingSurveillance',
    CYBER_SURVEILLANCE: 'cyberSurveillance',
    REPORTING: 'reporting',
    TASK_MANAGER: 'taskManager'
  };

  const [itemSidebarSelected, setItemSidebarSelected] = useState(SIDEBAR_KEYS.GENERAL);
  const [risposte, setRisposte] = useState(null);
  const [nis2Report, setNis2Report] = useState(null);

  const disableForEnterprise = useRecoilValue(disableForEnterpriseAtom)

  const loader = <div className='w-full h-full flex justify-center items-center'><ClipLoader size={60} /></div>

  useEffect(() => {
    const id = questionarioCurrent?.id;
    if (!id) return;

    const fetchRisposte = async () => {

      try {
        const res = await fetchWithAuth(API_ENDPOINTS.questionarioRisposte(id));
        if (res.status === 404) {
          setRisposte([]);
        } else if (res.ok) {
          setRisposte(await res.json());
        } else {
          console.error('Errore fetch risposte:', await res.text());
        }
      } catch (err) {
        console.error('Fetch risposte fallito:', err);
      }
    };

    const fetchNis2 = async () => {
 
      try {
        const res = await fetchWithAuth(API_ENDPOINTS.nis2Report(id));
        if (res.status === 404) {
          setNis2Report({});
        } else if (res.ok) {
          setNis2Report(await res.json());
        } else {
          console.error('Errore fetch NIS2:', await res.text());
        }
      } catch (err) {
        console.error('Fetch NIS2 fallito:', err);
      }
    };

    fetchRisposte();
    fetchNis2();
  }, [questionarioCurrent?.id]);

  const sidebarItems = [
    { key: SIDEBAR_KEYS.GENERAL, label: t('GeneralCompliance.title') },
    { key: SIDEBAR_KEYS.AREAS, label: t('ComplianceAreas.title') },
    { key: SIDEBAR_KEYS.AWARENESS, label: t('Awareness.title') },
    { key: SIDEBAR_KEYS.AWARENESS_TRAINING, label: t('AwarenessTraining.title') },
    { key: SIDEBAR_KEYS.NETWORKING_SURVEILLANCE, label: t('NetworkingSurveillance.title') },
    { key: SIDEBAR_KEYS.FILE_ANGEL, label: t('FileAngel.title') },
    { key: SIDEBAR_KEYS.BROWSING_ANGEL, label: t('BrowsingAngel.title') },
    { key: SIDEBAR_KEYS.CYBER_SURVEILLANCE, label: t('CyberSurveillance.title') },
    { key: SIDEBAR_KEYS.REPORTING, label: t('Reporting.title') },
  ];

  const bannerConfig = {
    [SIDEBAR_KEYS.GENERAL]: {
      title: t('GeneralCompliance.title'),
      description: t('GeneralCompliance.description'),
    },
    [SIDEBAR_KEYS.AREAS]: {
      title: t('ComplianceAreas.title'),
      description: t('ComplianceAreas.description'),
    },
    [SIDEBAR_KEYS.AWARENESS]: {
      title: t('Awareness.title'),
      description: t('Awareness.description'),
    },
    [SIDEBAR_KEYS.AWARENESS_TRAINING]: {
      title: t('AwarenessTraining.title'),
      description: t('AwarenessTraining.description'),
    },
    [SIDEBAR_KEYS.NETWORKING_SURVEILLANCE]: {
      title: t('NetworkingSurveillance.title'),
      description: t('NetworkingSurveillance.description'),
    },
    [SIDEBAR_KEYS.FILE_ANGEL]: {
      title: t('FileAngel.title'),
      description: t('FileAngel.description'),
    },
    [SIDEBAR_KEYS.BROWSING_ANGEL]: {
      title: t('BrowsingAngel.title'),
      description: t('BrowsingAngel.description'),
    },
    [SIDEBAR_KEYS.CYBER_SURVEILLANCE]: {
      title: t('CyberSurveillance.title'),
      description: t('CyberSurveillance.description'),
    },
    [SIDEBAR_KEYS.REPORTING]: {
      title: t('Reporting.title'),
      description: t('Reporting.description'),
    },
    [SIDEBAR_KEYS.TASK_MANAGER]: {
      title: t('TaskManager.title'),
      description: t('TaskManager.description'),
    },
  };

  const selectedBanner = bannerConfig[itemSidebarSelected] || {};
  const { title, description } = selectedBanner;

  const onExportPdf = async (checkedList) => {


    const payload = {
      flags: checkedList,
      lang: i18n.language,
    };


    try {
      const res = await fetchWithAuth(API_ENDPOINTS.exportPDF, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Errore durante la generazione del PDF');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = String(now.getFullYear()).slice(-2);
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');

      const filename = `CyberLog ${aziendaDettagli.nome_azienda} (Data ${day}-${month}-${year})(Orario ${hours}-${minutes}).pdf`;
      a.download = filename;

      a.click();
      window.URL.revokeObjectURL(url); // pulizia
    } catch (err) {
      const errorMessage = err.message || "Non è stato possibile scaricare il PDF.";
      toast.error(errorMessage);
    }
  };





  const renderContent = () => {
    switch (itemSidebarSelected) {
      case SIDEBAR_KEYS.GENERAL:
        return questionarioCurrent?.stato.toLowerCase() === 'completato' ?
          <div className='h-full flex justify-center items-center bg-white p-7 rounded-md border'>
            <Nis2Risk nis2ReportData={nis2Report} isVisibleDescriptionBanner={false} isVisibleDownloadReport={false} />
          </div>
          :
          questionarioCurrent?.stato.toLowerCase() === 'in_corso' ?
            <div className='text-yellow-700 flex gap-2 items-center'>
              <div className='text-yellow-600'>
                <Icon name={'warning'} />
              </div>
              <p>
                {t('messages.completeQuestionnaireToViewData')}
              </p>
            </div>
            : loader

      case SIDEBAR_KEYS.AREAS:
        return questionarioCurrent?.stato.toLowerCase() === 'completato' ?
            <SecurityTabs risposte={risposte} visibleQuestions={false} />
          :
          questionarioCurrent?.stato.toLowerCase() === 'in_corso' ?
            <div className='text-yellow-700 flex gap-2 items-center'>
              <div className='text-yellow-600'>
                <Icon name={'warning'} />
              </div>
              <p>
                {t('messages.completeQuestionnaireToViewData')}
              </p>
            </div>
            : loader

      case SIDEBAR_KEYS.AWARENESS:
        return questionarioCurrent?.stato.toLowerCase() === 'completato' ?
          <div className='h-full flex justify-center items-center bg-white p-7 rounded-md border'>
            <AwarenessCard percentage={nis2Report.indice_consapevolezza} />
          </div>
          :
          questionarioCurrent?.stato.toLowerCase() === 'in_corso' ?
            <div className='text-yellow-700 flex gap-2 items-center'>
              <div className='text-yellow-600'>
                <Icon name={'warning'} />
              </div>
              <p>
                {t('messages.completeQuestionnaireToViewData')}
              </p>
            </div>
            : loader

      case SIDEBAR_KEYS.AWARENESS_TRAINING:
        return <AwarenessTrainingStats />

      case SIDEBAR_KEYS.NETWORKING_SURVEILLANCE:
        return <NetworkingSurveillanceSection />

      case SIDEBAR_KEYS.FILE_ANGEL:
        return aziendaFileAngelStats ? <FileAngelTable fileAngelStats={aziendaFileAngelStats} viewBackground={false} viewDescriptionBanner={false}  disableForEnterprise={disableForEnterprise}/> : 
        (aziendaFileAngelStats === null ? <div className='text-center py-8 text-gray-500'>{t('cyberLogMessages.noDataAvailable')}</div> : loader)

      case SIDEBAR_KEYS.BROWSING_ANGEL:
        return browsingAngelStats ? <BrowsingAngelTable browsingAngelStats={browsingAngelStats} viewBackground={false} viewDescriptionBanner={false} disableForEnterprise={disableForEnterprise}/> :
        (browsingAngelStats === null ? <div className='text-center py-8 text-gray-500'>{t('cyberLogMessages.noDataAvailable')}</div> : loader)

      case SIDEBAR_KEYS.CYBER_SURVEILLANCE:
        return (
          <div className="space-y-6">
            {cyberSurveillanceData ? <CyberSurveillanceDashboard cyberSurveillanceResult={cyberSurveillanceData} /> : loader}
            
            {/* Sezione minacce e grafici */}
            {cyberSurveillanceData && allVulnerabilities && allVulnerabilities.length > 0 ? (
              <div className="w-full">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-4 md:gap-6 auto-rows-fr">
                  {/* Stats minacce - card individuali che occupano 1 colonna ciascuna */}
                  <CyberSurveillanceStats cyberSurveillanceResult={cyberSurveillanceData} />
                  
                  {/* Grafici - card individuali che occupano 1 colonna ciascuna */}
                  <VulnerabilityCharts 
                    key={JSON.stringify(allVulnerabilities.map(v => `${v.id}-${v.status}`))}
                    vulnerabilities={allVulnerabilities} 
                  />
                </div>
              </div>
            ) : allVulnerabilitiesAPI ? (
              <div className="text-center py-8 text-gray-500">
                {t('cyberLogMessages.noVulnerabilities')}
              </div>
            ) : loader}
          </div>
        )

      case SIDEBAR_KEYS.REPORTING:
        return <ReportExporter onExportPdf={onExportPdf} />
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-6 items-stretch">

      <div>
        <DescriptionBanner title="Cyber Log" description={t('DescrBann_CyberLog')} />
      </div>

      <div className="flex flex-col md:flex-row bg-[#F9FAFB] min-h-[32rem] md:max-h-[calc(100vh-12rem)]">

        {/* Sidebar */}
        <div className="w-full md:w-60 bg-white shadow p-3 md:p-5 overflow-x-auto md:overflow-y-auto flex md:flex-col gap-2 md:gap-0 flex-nowrap" data-tour="cyberlog-sidebar">
          {sidebarItems.map((item) => (
            <button
              key={item.key}
              onClick={() => setItemSidebarSelected(item.key)}
              data-tour={`cyberlog-btn-${item.key}`}
              className={`
                flex gap-2 mb-3 items-center font-semibold shrink-0 md:shrink md:w-full px-3 py-2 md:px-2 md:py-1 text-gray-700 rounded transition-colors text-xs sm:text-sm whitespace-nowrap md:whitespace-normal
                ${itemSidebarSelected === item.key ? 'bg-yellow-400 text-black' : 'hover:bg-yellow-100 hover:text-black'}`}
            >
              {getCyberLogIcon(item.key, { className: 'w-5 h-5' })}
              {item.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col px-2 sm:px-6 overflow-y-auto" data-tour="cyberlog-content">
          <div className="flex flex-col">
            <DescriptionBanner title={title} description={description} />
            <div className="flex-1 mt-8 pb-4" data-tour={`cyberlog-view-${itemSidebarSelected}`}>
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
